import React, { useCallback } from 'react';
import { GenericCard } from 'components/display/Card/Card';
import { NsKey } from 'enums/NsKey';
import { TitleIconFactory } from 'factories/TitleIconFactory';
import { TitleIconKey } from 'enums/titleIconKey';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import ConfettiSuccess from 'components/display/ConfettiSuccess';
import { contractsAPI } from 'services/swagger-api';
import {
  useBorrowerSortedDocuments,
  useBorrowerIsFetchingDocuments,
  useBorrowerIsAnimationCompleted,
  useBorrowerHasCertificate,
  useBorrowerEmailConfig,
  useBorrowerId
} from '../../store';
import { useSubscriptionProcessActions } from 'features/subscription-process/contexts/SubscriptionProcessContext';
import { useBorrowerDocumentsSync } from '../../hooks/useBorrowerDocumentsSync';
import { DocumentsList } from '../documents/DocumentsList';
import { DocumentCompletionBanner } from '../documents/DocumentCompletionBanner';
import { CardsSection } from 'components';

interface BorrowerDocumentsProps {
  className?: string;
}

const BorrowerDocuments: React.FC<BorrowerDocumentsProps> = ({ className }) => {
  const borrowerId = useBorrowerId();
  const isFetchingDocuments = useBorrowerIsFetchingDocuments();
  const isAnimationCompleted = useBorrowerIsAnimationCompleted();
  const hasCertificate = useBorrowerHasCertificate();
  const emailConfig = useBorrowerEmailConfig();
  const { refreshSubscriptionProcess } = useSubscriptionProcessActions();
  const { t } = useDynamicTranslation([
    NsKey.Contractualization,
    NsKey.Document
  ]);

  // Hook centralisé pour la synchronisation des documents (fetch initial + SignalR)
  useBorrowerDocumentsSync();

  // Documents triés
  const sortedDocuments = useBorrowerSortedDocuments();

  const patchAnimationCompleted = useCallback(async () => {
    if (borrowerId) {
      await contractsAPI.patchContractAnimation({ riskId: borrowerId });
    }
    refreshSubscriptionProcess();
  }, [borrowerId, refreshSubscriptionProcess]);

  return (
    <CardsSection title={t('CONTRACTUALIZATION.TITLE.DOCUMENT_MANAGE')}>
      <DocumentsList
        documents={sortedDocuments}
        isFetchingDocuments={isFetchingDocuments}
        isSendingContract={false}
        emailConfig={emailConfig}
      />
      {hasCertificate && !isAnimationCompleted && (
        <ConfettiSuccess
          count={100}
          duration={4000}
          onComplete={patchAnimationCompleted}
        />
      )}
      {hasCertificate && <DocumentCompletionBanner />}
    </CardsSection>
  );
};

export default BorrowerDocuments;
